import Cookies from "js-cookie";

import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import {
    LP_VISITED_COOKIE,
    OESTRA_QUIZ_LP_REDIRECT_URL,
} from "~/constants/oestraLandingPages";
import { useNavigationStore } from "~/stores/useNavigationStore";
import { useQuizStore } from "~/stores/useQuizStore";

export type OestraRedirectState = {
    hasVisitedLp: boolean;
    cameFromTarget: boolean;
    isOestraQuizStarted: boolean;
    isNewQuizStarted: boolean;
};

export const OESTRA_LP_DESTINATION_KIND = {
    NEW_QUIZ: "newQuiz",
    LP: "lp",
} as const;

export type OestraLpDestinationKind =
    (typeof OESTRA_LP_DESTINATION_KIND)[keyof typeof OESTRA_LP_DESTINATION_KIND];

export const getOestraLpDestinationUrl = (kind: OestraLpDestinationKind): string =>
    kind === OESTRA_LP_DESTINATION_KIND.NEW_QUIZ
        ? QUIZ_ROUTES.BIOIDENTICAL_HRT
        : OESTRA_QUIZ_LP_REDIRECT_URL;

const isQuizStarted = (category: string) =>
    (useQuizStore.getState().getQuiz(category)?.pageNo ?? 0) > 0;

const oestraLpCookieListeners = new Set<() => void>();

/** Client cookie booleans for `useSyncExternalStore` (primitive snapshots). */
export const getHasVisitedLpCookie = (): boolean => !!Cookies.get(LP_VISITED_COOKIE);

export const subscribeOestraLpRedirectCookies = (onStoreChange: () => void) => {
    oestraLpCookieListeners.add(onStoreChange);
    return () => {
        oestraLpCookieListeners.delete(onStoreChange);
    };
};

/** Call after mutating LP redirect cookies so mounted quiz CTAs re-read them. */
export const notifyOestraLpRedirectCookiesChanged = () => {
    oestraLpCookieListeners.forEach((listener) => {
        listener();
    });
};

/**
 * Snapshot the cookie and store values the oestra-LP redirect decision needs.
 * Client-only — on the server `Cookies.get` returns `undefined` and stores hold
 * their initial state, so every field reads `false`.
 */
export const readOestraRedirectState = (): OestraRedirectState => ({
    hasVisitedLp: getHasVisitedLpCookie(),
    // The navigation store only advances after the new page commits, so during the
    // quiz's first render `currentPath` is still the page the visitor came from.
    cameFromTarget: useNavigationStore.getState().currentPath === OESTRA_QUIZ_LP_REDIRECT_URL,
    isOestraQuizStarted: isQuizStarted(QUIZ_CATEGORIES.OESTRA_CREAM),
    isNewQuizStarted: isQuizStarted(QUIZ_CATEGORIES.BIOIDENTICAL_HRT),
});

/**
 * Given an already-captured redirect state, return the destination kind the
 * visitor should be sent to, or `null` when no redirect applies.
 *
 * A visitor who saw an existing LP goes to the new quiz, unless they already
 * progressed in this quiz. A visitor who answered questions in the new quiz, or
 * comes straight from the target LP without progress in this quiz, also goes to
 * the new quiz (sending the second case back to the LP would loop). Otherwise a
 * visitor with no LP visit and no progress in this quiz goes to the LP.
 */
export const resolveOestraLpDestination = (state: OestraRedirectState): OestraLpDestinationKind | null => {
    const { hasVisitedLp, cameFromTarget, isOestraQuizStarted, isNewQuizStarted } = state;

    if (hasVisitedLp && !isOestraQuizStarted) return OESTRA_LP_DESTINATION_KIND.NEW_QUIZ;

    const shouldOpenNewQuiz = isNewQuizStarted || (cameFromTarget && !isOestraQuizStarted);
    const isEligible = shouldOpenNewQuiz || (!hasVisitedLp && !isOestraQuizStarted);

    if (!isEligible) return null;

    return shouldOpenNewQuiz ? OESTRA_LP_DESTINATION_KIND.NEW_QUIZ : OESTRA_LP_DESTINATION_KIND.LP;
};
